import { useId, useMemo } from 'react'

/**
 * Gráfico de evolução, em SVG puro.
 *
 * Sem biblioteca, e não por economia: uma lib de gráficos custa
 * 40–150 KB para desenhar uma linha com sete pontos. O que este
 * gráfico precisa fazer é responder "melhorou?" de longe.
 *
 * Duas decisões que mudam a leitura:
 *
 * · **A escala vem do conceito, não dos dados.** Dor é 0–10 sempre. Se
 *   o eixo se ajustasse aos valores, uma variação de 7 para 6 ocuparia
 *   a altura inteira e pareceria uma queda enorme.
 * · **O último ponto é destacado.** É o valor de hoje — o que a
 *   pessoa está procurando quando abre a tela.
 */

type Ponto = { x: number; y: number }

export function Grafico({
  titulo, unidade, pontos, minimo, maximo, altura = 132,
}: {
  titulo: string
  unidade: string | null
  pontos: Ponto[]
  minimo: number | null
  maximo: number | null
  altura?: number
}) {
  const id = useId()
  const L = 34, R = 10, T = 12, B = 22   // margens
  const W = 320

  const { caminho, area, marcas, escala } = useMemo(() => {
    const ord = [...pontos].sort((a, b) => a.x - b.x)
    const xs = ord.map((p) => p.x)
    const x0 = Math.min(...xs), x1 = Math.max(...xs)

    // A faixa canônica manda. Só se ela não existir é que os dados
    // definem a escala — e ainda assim com folga de 10%.
    let y0 = minimo, y1 = maximo
    if (y0 == null || y1 == null) {
      const ys = ord.map((p) => p.y)
      const lo = Math.min(...ys), hi = Math.max(...ys)
      const folga = (hi - lo) * 0.1 || 1
      y0 = lo - folga; y1 = hi + folga
    }

    const px = (x: number) =>
      L + (x1 === x0 ? (W - L - R) / 2 : ((x - x0) / (x1 - x0)) * (W - L - R))
    const py = (y: number) =>
      T + (1 - (y - y0!) / ((y1! - y0!) || 1)) * (altura - T - B)

    const d = ord.map((p, i) => `${i ? 'L' : 'M'}${px(p.x)},${py(p.y)}`).join(' ')
    const base = altura - B
    const a = ord.length
      ? `${d} L${px(ord[ord.length - 1]!.x)},${base} L${px(ord[0]!.x)},${base} Z`
      : ''

    return {
      caminho: d, area: a,
      marcas: ord.map((p) => ({ cx: px(p.x), cy: py(p.y), v: p.y, t: p.x })),
      escala: { y0: y0!, y1: y1! },
    }
  }, [pontos, minimo, maximo, altura])

  const ultimo = marcas[marcas.length - 1]
  const primeiro = marcas[0]
  const melhorou = ultimo && primeiro ? ultimo.v < primeiro.v : false

  return (
    <figure className="grafico">
      <figcaption>
        <span className="grafico__titulo">{titulo}</span>
        {ultimo && (
          <span className="grafico__agora">
            {ultimo.v}{unidade && unidade !== 'escala' ? ` ${unidade}` : ''}
            {primeiro && ultimo.v !== primeiro.v && (
              <span className={melhorou ? 'grafico__delta--bom' : 'grafico__delta--ruim'}>
                {' '}{melhorou ? '↓' : '↑'} {Math.abs(ultimo.v - primeiro.v)}
              </span>
            )}
          </span>
        )}
      </figcaption>

      <svg viewBox={`0 0 ${W} ${altura}`} width="100%" height={altura}
           role="img"
           aria-label={`${titulo}: de ${primeiro?.v ?? '—'} para ${ultimo?.v ?? '—'}`}>
        <defs>
          <linearGradient id={`g${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--cl-primaria)" stopOpacity="0.18" />
            <stop offset="100%" stopColor="var(--cl-primaria)" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Só três linhas de grade. Mais que isso vira gaiola. */}
        {[0, 0.5, 1].map((f) => {
          const y = T + f * (altura - T - B)
          const v = escala.y1 - f * (escala.y1 - escala.y0)
          return (
            <g key={f}>
              <line x1={L} y1={y} x2={W - R} y2={y}
                    stroke="var(--cl-linha)" strokeWidth="1" />
              <text x={L - 6} y={y + 3} textAnchor="end"
                    fontSize="9" fill="var(--cl-texto-3)"
                    fontFamily="var(--cl-fonte-dado)">
                {Number.isInteger(v) ? v : v.toFixed(1)}
              </text>
            </g>
          )
        })}

        <path d={area} fill={`url(#g${id})`} />
        <path d={caminho} fill="none" stroke="var(--cl-primaria)"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

        {marcas.map((m, i) => (
          <circle key={i} cx={m.cx} cy={m.cy}
                  r={i === marcas.length - 1 ? 4.5 : 2.5}
                  fill={i === marcas.length - 1 ? 'var(--cl-primaria)' : 'var(--cl-superficie)'}
                  stroke="var(--cl-primaria)" strokeWidth="2">
            <title>
              {new Date(m.t).toLocaleDateString('pt-BR')}: {m.v}
            </title>
          </circle>
        ))}

        {primeiro && ultimo && (
          <>
            <text x={primeiro.cx} y={altura - 6} fontSize="9"
                  textAnchor="start" fill="var(--cl-texto-3)"
                  fontFamily="var(--cl-fonte-dado)">
              {new Date(primeiro.t).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
            </text>
            <text x={ultimo.cx} y={altura - 6} fontSize="9"
                  textAnchor="end" fill="var(--cl-texto-3)"
                  fontFamily="var(--cl-fonte-dado)">
              {new Date(ultimo.t).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
            </text>
          </>
        )}
      </svg>

      {/* A tabela é a mesma informação para quem usa leitor de tela —
          um SVG sozinho não é lido. */}
      <table className="oculto-visual">
        <caption>{titulo}</caption>
        <tbody>
          {marcas.map((m, i) => (
            <tr key={i}>
              <th scope="row">{new Date(m.t).toLocaleDateString('pt-BR')}</th>
              <td>{m.v}{unidade ? ` ${unidade}` : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  )
}
